/**
 * How long an item has sat without its owner doing anything about it.
 *
 * Trellis reports "unattended for two days" on a lead's view
 * (docs/2026-09-24-unconfirmed-work-is-visible.md). `updatedAt` cannot answer that:
 * a roster refresh, a reprogram, a lead's note or any other system write bumps it,
 * so an item nobody has answered can look freshly handled. The audit log can answer
 * it, because every event carries who caused it.
 *
 * So the clock is the newest event the **owner** caused. A lead chasing somebody
 * deliberately does not reset it: the `noted` event a lead writes carries the lead
 * as `by`, and a nudge is not an answer.
 */
import type { Audit, Commitment, Iso, PersonId } from "./commitment.js";
import { parseDuration } from "./dates.js";

/**
 * Answering it: taking it, refusing it, passing it on or re-dating it. Writing a note
 * or changing its wording is engagement, not an answer, and treating those as one
 * would let an item sit for ever while looking attended to. `acknowledged` counts
 * because for a customer ask that is the defined response and it stops the engagement
 * clock too.
 */
const OWNER_ANSWERED = new Set([
  "confirmed", "accepted", "declined", "set-aside", "not-mine", "restored",
  "moved", "closed", "acknowledged", "reassigned",
  "transfer-requested", "transfer-accepted", "transfer-declined", "delegated",
]);

/** When the owner last did something about it, or undefined if they never have. */
export function lastOwnerAction(c: Commitment, events: Audit[]): Iso | undefined {
  let latest: Iso | undefined;
  for (const e of events) {
    if (e.commitmentId !== c.id) continue;
    const by = (e.event as { by?: PersonId | "trellis" }).by;
    if (by !== c.owner) continue;
    if (!OWNER_ANSWERED.has(e.event.type)) continue;
    if (!latest || e.at > latest) latest = e.at;
  }
  return latest;
}

/** Events that hand an item to somebody: the clock starts again for the new owner. */
const HANDED_OVER = new Set(["assigned", "reassigned", "proposed", "transfer-requested", "delegated"]);

/** When this owner got it: the latest hand-over to them, else when it was captured. */
export function reachedOwner(c: Commitment, events: Audit[]): Iso {
  let latest: Iso | undefined;
  for (const e of events) {
    if (e.commitmentId !== c.id) continue;
    if (!HANDED_OVER.has(e.event.type)) continue;
    if ((e.event as { to?: PersonId }).to !== c.owner) continue;
    if (!latest || e.at > latest) latest = e.at;
  }
  return latest ?? c.createdAt;
}

/**
 * When the clock started: the owner's last action, else when the item reached them.
 * An ask captured a week ago and assigned this morning has waited since this morning,
 * not since it was captured, or every reassignment would raise a false alarm.
 */
export function waitingSince(c: Commitment, events: Audit[]): Iso {
  const action = lastOwnerAction(c, events);
  const reached = reachedOwner(c, events);
  return action && action > reached ? action : reached;
}

/**
 * Whole days the item has waited on its owner, or undefined when it is not waiting on
 * them at all. A closed item is finished; an open one is on its date, not on a clock.
 * Only work the owner has yet to accept or refuse is waiting.
 */
export function unattendedDays(c: Commitment, events: Audit[], now: Date): number | undefined {
  if (c.status !== "candidate" && c.status !== "intake") return undefined;
  const since = Date.parse(waitingSince(c, events));
  if (Number.isNaN(since)) return undefined;
  const days = Math.floor((now.getTime() - since) / 86_400_000);
  return days < 0 ? 0 : days;
}

/**
 * The threshold in milliseconds. Any duration the model accepts works, `PT8H` and `P1W`
 * among them; a programme that sets one should not have it quietly ignored. Anything
 * unparseable falls back rather than throwing, because a bad parameter should not take
 * a page down.
 */
export function thresholdMs(iso: unknown, fallback = UNATTENDED_AFTER_DEFAULT_MS): number {
  // The value comes out of jsonb, so it is not necessarily a string.
  if (typeof iso !== "string" || !iso.trim()) return fallback;
  try { return parseDuration(iso); } catch { return fallback; }
}

export const UNATTENDED_AFTER_DEFAULT_MS = 2 * 86_400_000;

/** Items on a lead's view that nobody has answered for long enough to say so. */
export function unattended(
  commitments: Commitment[],
  events: Audit[],
  now: Date,
  afterMs: number = UNATTENDED_AFTER_DEFAULT_MS,
): { commitment: Commitment; days: number; since: Iso }[] {
  const out: { commitment: Commitment; days: number; since: Iso }[] = [];
  for (const c of commitments) {
    if (c.status !== "candidate" && c.status !== "intake") continue;
    const since = waitingSince(c, events);
    const waited = now.getTime() - Date.parse(since);
    if (Number.isNaN(waited) || waited < afterMs) continue;
    out.push({ commitment: c, days: Math.max(0, Math.floor(waited / 86_400_000)), since });
  }
  return out.sort((a, b) => b.days - a.days || a.commitment.title.localeCompare(b.commitment.title));
}
